import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Accordion } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import Pagination from "../components/Pagination";
import toast from "react-hot-toast";
import { deleteFAQS, getFAQS, postFAQS, updateFAQS } from "../../services/UserServices/UserService";
import PageTitle from "../layouts/PageTitle";

const FAQS = () => {
  const [faq, setFaq] = useState([]);
  const [openFaqId, setOpenFaqId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const totalItems = faq.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedFaqs = faq.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState(null);

  const confirmDelete = async () => {
    try {
      const { id } = faqToDelete;
      setFaq((prev) => prev.filter((f) => f._id !== id));
      await deleteFAQS(id);
      toast.success("Deleted Successfully");
      if (openFaqId === id) setOpenFaqId(null);
    } catch (error) {
      toast.error("Failed to delete FAQ");
    } finally {
      setShowDeleteModal(false);
      setFaqToDelete(null);
    }
  };

  const requiredTrimmed = Yup.string()
    .trim()
    .min(1, "This field cannot be empty")
    .required("This field is required");

  const validationSchema = Yup.object({
    question: requiredTrimmed,
    answer: requiredTrimmed,
    question_hi: requiredTrimmed,
    answer_hi: requiredTrimmed,
    question_pa: requiredTrimmed,
    answer_pa: requiredTrimmed,
    question_gu: requiredTrimmed,
    answer_gu: requiredTrimmed,
    question_mr: requiredTrimmed,
    answer_mr: requiredTrimmed,
    question_te: requiredTrimmed,
    answer_te: requiredTrimmed,
    question_kn: requiredTrimmed,
    answer_kn: requiredTrimmed,
    question_ta: requiredTrimmed,
    answer_ta: requiredTrimmed,
    question_ml: requiredTrimmed,
    answer_ml: requiredTrimmed,
  });

  const getFaqs = async () => {
    try {
      const response = await getFAQS();
      setFaq(response?.data.data);
    } catch (error) {
      toast.error("Failed to load FAQs");
    }
  };

  useEffect(() => {
    getFaqs();
  }, []);

  const handleEdit = (item) => {
    setSelectedFaq(item);
    setShowEditModal(true);
  };

  const handleUpdate = async (values, { setSubmitting }) => {
    try {
      await updateFAQS(values);
      getFaqs();
      setShowEditModal(false);
      toast.success("Updated Successfully");
      setSubmitting(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update FAQ");
      setSubmitting(false);
    }
  };

  const handleAdd = async (values, { setSubmitting, resetForm }) => {
    try {
      await postFAQS(values);
      getFaqs();
      setShowAddModal(false);
      toast.success("New FAQ Added Successfully");
      resetForm();
      setSubmitting(false);
    } catch (error) {
      toast.error("Failed to add FAQ");
      setSubmitting(false);
    }
  };

  const languages = [
    { code: "en", label: "English" },
    { code: "hi", label: "Hindi" },
    { code: "pa", label: "Punjabi" },
    { code: "gu", label: "Gujarati" },
    { code: "mr", label: "Marathi" },
    { code: "te", label: "Telugu" },
    { code: "kn", label: "Kannada" },
    { code: "ta", label: "Tamil" },
    { code: "ml", label: "Malayalam" },
  ];

  const otherLanguages = [
    { code: "hi", label: "Hindi", questionKey: "question_hi", answerKey: "answer_hi" },
    { code: "pa", label: "Punjabi", questionKey: "question_pa", answerKey: "answer_pa" },
    { code: "gu", label: "Gujarati", questionKey: "question_gu", answerKey: "answer_gu" },
    { code: "mr", label: "Marathi", questionKey: "question_mr", answerKey: "answer_mr" },
    { code: "te", label: "Telugu", questionKey: "question_te", answerKey: "answer_te" },
    { code: "kn", label: "Kannada", questionKey: "question_kn", answerKey: "answer_kn" },
    { code: "ta", label: "Tamil", questionKey: "question_ta", answerKey: "answer_ta" },
    { code: "ml", label: "Malayalam", questionKey: "question_ml", answerKey: "answer_ml" },
  ];

  const renderFormSection = (language) => {
    const questionField = language.code === "en" ? "question" : `question_${language.code}`;
    const answerField = language.code === "en" ? "answer" : `answer_${language.code}`;

    return (
      <div key={language.code} className="qx-faq-form-section">
        <h6>{language.label}</h6>

        <Form.Group className="mb-3">
          <Form.Label>Question</Form.Label>
          <Field
            name={questionField}
            className="form-control"
            placeholder={`Enter question in ${language.label}`}
          />
          <div className="text-danger small mt-1">
            <ErrorMessage name={questionField} />
          </div>
        </Form.Group>

        <Form.Group className="mb-0">
          <Form.Label>Answer</Form.Label>
          <Field
            as="textarea"
            name={answerField}
            className="form-control"
            rows={4}
            placeholder={`Enter answer in ${language.label}`}
          />
          <div className="text-danger small mt-1">
            <ErrorMessage name={answerField} />
          </div>
        </Form.Group>
      </div>
    );
  };

  const renderFaqBody = (item) => (
    <>
      <p className="qx-faq-answer-label">English Answer</p>
      <p className="qx-faq-answer-text">{item.answer}</p>

      <div className="qx-faq-lang-grid">
        {otherLanguages.map((lang) => (
          <div key={lang.code} className="qx-faq-lang-block">
            <h6>{lang.label}</h6>
            <p><strong>Q:</strong> {item[lang.questionKey]}</p>
            <p><strong>A:</strong> {item[lang.answerKey]}</p>
          </div>
        ))}
      </div>

      <div className="qx-faq-actions">
        <Button type="button" className="btn-submit" size="sm" onClick={() => handleEdit(item)}>
          Edit
        </Button>
        <Button
          type="button"
          variant="outline-secondary"
          className="btn-cancel"
          size="sm"
          onClick={() => {
            setFaqToDelete({ id: item._id });
            setShowDeleteModal(true);
          }}
        >
          Delete
        </Button>
      </div>
    </>
  );

  return (
    <div className="qx-page">
      <PageTitle
        pageTitle="FAQs Management"
        subtitle="Manage your frequently asked questions across multiple languages"
        actions={
          <Button type="button" className="btn-primary qx-btn-brand" onClick={() => setShowAddModal(true)}>
            + Add FAQs
          </Button>
        }
      />

      {faq.length > 0 && (
        <div className="qx-filter-card p-3 mb-4">
          <p className="mb-0" style={{ fontSize: "14px", color: "#667085" }}>
            Total FAQs: <span style={{ fontWeight: 600, color: "#27272a" }}>{faq.length}</span>
          </p>
        </div>
      )}

      {paginatedFaqs.length === 0 ? (
        <div className="qx-empty-state">
          <p className="mb-1" style={{ fontWeight: 500, color: "#27272a" }}>No FAQs Available</p>
          <p className="mb-0">Get started by adding your first FAQ</p>
        </div>
      ) : (
        <div className="qx-accordion-card">
          <Accordion
            flush
            className="qx-accordion"
            activeKey={openFaqId}
            onSelect={(key) => setOpenFaqId(key === openFaqId ? null : key)}
          >
            {paginatedFaqs.map((item) => (
              <Accordion.Item eventKey={item._id} key={item._id}>
                <Accordion.Header>{item.question}</Accordion.Header>
                <Accordion.Body>{renderFaqBody(item)}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
          {totalPages > 1 && (
            <Pagination
              pageCount={totalPages}
              pageValue={currentPage}
              setPage={setCurrentPage}
              total={totalItems}
              pageSize={itemsPerPage}
              itemLabel="FAQs"
            />
          )}
        </div>
      )}

      {selectedFaq && (
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          centered
          className="qx-modal"
          dialogClassName="qx-modal-dialog"
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit FAQ</Modal.Title>
          </Modal.Header>
          <Formik
            enableReinitialize
            initialValues={{
              faqId: selectedFaq._id,
              question: selectedFaq.question || "",
              answer: selectedFaq.answer || "",
              question_hi: selectedFaq.question_hi || "",
              answer_hi: selectedFaq.answer_hi || "",
              question_pa: selectedFaq.question_pa || "",
              answer_pa: selectedFaq.answer_pa || "",
              question_gu: selectedFaq.question_gu || "",
              answer_gu: selectedFaq.answer_gu || "",
              question_mr: selectedFaq.question_mr || "",
              answer_mr: selectedFaq.answer_mr || "",
              question_te: selectedFaq.question_te || "",
              answer_te: selectedFaq.answer_te || "",
              question_kn: selectedFaq.question_kn || "",
              answer_kn: selectedFaq.answer_kn || "",
              question_ta: selectedFaq.question_ta || "",
              answer_ta: selectedFaq.answer_ta || "",
              question_ml: selectedFaq.question_ml || "",
              answer_ml: selectedFaq.answer_ml || "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleUpdate}
          >
            {({ isSubmitting }) => (
              <FormikForm>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  {languages.map((lang) => renderFormSection(lang))}
                </Modal.Body>
                <Modal.Footer>
                  <Button type="button" variant="outline-secondary" className="btn-cancel" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-submit" disabled={isSubmitting}>
                    {isSubmitting ? "Updating..." : "Save Changes"}
                  </Button>
                </Modal.Footer>
              </FormikForm>
            )}
          </Formik>
        </Modal>
      )}

      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
        className="qx-modal"
        dialogClassName="qx-modal-dialog"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New FAQ</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            question: "",
            answer: "",
            question_hi: "",
            answer_hi: "",
            question_pa: "",
            answer_pa: "",
            question_gu: "",
            answer_gu: "",
            question_mr: "",
            answer_mr: "",
            question_te: "",
            answer_te: "",
            question_kn: "",
            answer_kn: "",
            question_ta: "",
            answer_ta: "",
            question_ml: "",
            answer_ml: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleAdd}
        >
          {({ isSubmitting }) => (
            <FormikForm>
              <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {languages.map((lang) => renderFormSection(lang))}
              </Modal.Body>
              <Modal.Footer>
                <Button type="button" variant="outline-secondary" className="btn-cancel" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? "Adding..." : "Add FAQ"}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="qx-modal" dialogClassName="qx-modal-dialog">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2" style={{ color: "#344054", fontSize: "14px" }}>
            Are you sure you want to delete this FAQ?
          </p>
          <p className="mb-0 small text-muted">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" className="btn-cancel" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button type="button" className="btn-submit" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FAQS;
